import { cn } from '@spiaggeit/spit-ui'
import { ComponentPropsWithoutRef, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon, Icon } from '@/assets/icons'

interface ExpandedSectionProps extends ComponentPropsWithoutRef<'p'> {
  isVisible: boolean
  onShowSection(): void
  text: string
  sectionAriaLabel: string
  tooltipText?: string
  iconName?: AppIcon
  textClassName?: string
}

export const ExpandedSection = (props: ExpandedSectionProps) => {
  const { t } = useTranslation()
  const {
    text,
    children,
    isVisible,
    sectionAriaLabel,
    onShowSection,
    className,
    tooltipText,
    iconName,
    textClassName,
    ...rest
  } = props

  const sectionId = useId()

  const ref = useRef<HTMLDivElement | null>(null)!

  const triggerContent = (
    <>
      {textClassName ? <span className={textClassName}>{text}</span> : text}{' '}
      <button
        aria-controls={sectionId}
        aria-expanded={isVisible}
        aria-label={tooltipText}
        className="font-bold text-link"
        onClick={onShowSection}
        tabIndex={isVisible ? -1 : 0}
        title={tooltipText}
        type="button"
      >
        {t('common.clickHere')}
      </button>
    </>
  )

  return (
    <div>
      <p
        aria-hidden={isVisible}
        {...rest}
        className={cn(
          'transition-all',
          {
            'pointer-events-none m-0 h-0 opacity-0': isVisible,
          },
          { 'flex items-center gap-2': !!iconName },
          className
        )}
      >
        {iconName ? (
          <>
            <Icon className="size-5 flex-shrink-0 text-brand" name={iconName} />
            <span>{triggerContent}</span>
          </>
        ) : (
          triggerContent
        )}
      </p>

      <section
        aria-label={sectionAriaLabel}
        className={cn(
          'flex transition-all',
          {
            'pointer-events-auto translate-y-0 opacity-100': isVisible,
            'pointer-events-none h-0 translate-y-4 opacity-0': !isVisible,
          },
          className
        )}
        id={sectionId}
        ref={ref}
      >
        {children}
      </section>
    </div>
  )
}
